import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Lock, Check, ArrowRight, Loader2 } from "lucide-react";
import { PLANS } from "../../data/pricing";
import { TIER_PLAN_KEY } from "../../data/tools";
import { scrollToId } from "../../hooks/useLenis";
import { startCheckout, formatApiError } from "../../lib/api";
import { toast } from "sonner";

const ACCENT = {
  "Débutant": "text-emerald-300 border-emerald-400/40 bg-emerald-400/10",
  "Expert": "text-blue-300 border-blue-400/40 bg-blue-400/10",
  "Premium": "text-amber-300 border-amber-400/40 bg-amber-400/10",
  "Pro": "text-orange-300 border-orange-400/40 bg-orange-400/10",
};

export const UpgradeModal = ({ open, tool, tier, onClose }) => {
  const planKey = TIER_PLAN_KEY[tier] || "plan_debutant";
  const plan = PLANS.find((p) => p.id === planKey);
  const accent = ACCENT[tier] || ACCENT["Débutant"];
  const [loading, setLoading] = useState(false);

  const subscribe = async () => {
    try {
      setLoading(true);
      // Redirect straight to the Stripe checkout for the tier's monthly subscription.
      await startCheckout(`${planKey}_monthly`);
    } catch (e) {
      setLoading(false);
      toast.error(formatApiError(e.response?.data?.detail) || "Impossible d'ouvrir le paiement Stripe.");
    }
  };

  const goToPricing = () => {
    onClose();
    setTimeout(() => scrollToId("pricing"), 250);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="upgrade-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-[115] flex items-end justify-center bg-black/65 p-4 backdrop-blur-sm sm:items-center"
        >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md overflow-hidden rounded-2xl border border-blue-500/25 bg-[#0B1220]/97 backdrop-blur-2xl shadow-2xl"
          >
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(37,99,235,0.15),transparent_60%)]" />
            <button data-testid="upgrade-close" onClick={onClose} className="absolute right-4 top-4 z-10 text-[#8AA0C0] hover:text-white">
              <X size={18} />
            </button>

            <div className="relative p-7">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-blue-400/40 bg-blue-500/10">
                <Lock size={20} className="text-blue-300" />
              </div>
              <h3 className="mt-5 font-display text-2xl font-800 tracking-tighter text-white">
                Débloquez « {tool?.name} »
              </h3>
              <p className="mt-2 font-body text-sm leading-relaxed text-[#8AA0C0]">
                Cet outil est inclus dans l'abonnement{" "}
                <span className={`rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em] ${accent}`}>{tier}</span>.
                Passez à ce forfait pour y accéder ainsi qu'à tous les outils des niveaux inférieurs.
              </p>

              {plan && (
                <div className="mt-6 rounded-xl border border-blue-500/20 bg-blue-400/[0.03] p-5">
                  <div className="flex items-baseline justify-between">
                    <span className="font-display text-lg font-700 text-white">{plan.name}</span>
                    <span className="font-display text-2xl font-800 tracking-tighter text-white">${plan.monthly}<span className="ml-1 font-mono text-[10px] uppercase tracking-[0.15em] text-[#8AA0C0]">/mois</span></span>
                  </div>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.12em] text-blue-300">{plan.credits} crédits / mois</p>
                  <ul className="mt-4 space-y-2">
                    {plan.features.slice(0, 4).map((f) => (
                      <li key={f} className="flex items-start gap-2 font-body text-xs text-[#CBD5E1]">
                        <Check size={13} className="mt-0.5 shrink-0 text-blue-300" /> {f}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <button
                data-testid="upgrade-cta"
                onClick={subscribe}
                disabled={loading}
                className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-blue-600 px-6 py-3.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white cta-glow transition-all hover:bg-blue-500 disabled:opacity-60"
              >
                {loading
                  ? <><Loader2 size={14} className="animate-spin" /> Redirection…</>
                  : <>S'abonner {plan ? `· $${plan.monthly}/mois` : ""} <ArrowRight size={14} /></>}
              </button>
              <button
                data-testid="upgrade-compare"
                onClick={goToPricing}
                className="mt-3 flex w-full items-center justify-center font-mono text-[9px] uppercase tracking-[0.2em] text-[#8AA0C0] transition-colors hover:text-blue-200"
              >
                Comparer tous les forfaits
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
